import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Mail, Lock, Building2, Users } from 'lucide-react';
import { MARKETING_OPT_IN_POINTS } from '@evi/shared';
import { consumePendingDeepLink } from '@/lib/pendingDeepLink';
import { captureReferralFromUrl, readPendingReferral } from '@/lib/pendingReferral';
import { FLEET_JOIN_CODE_STORAGE_KEY, normalizeFleetJoinCode, continueFleetInvite, clearStashedFleetInviteToken } from '@/pages/fleet/JoinFleetPage';
import { useTheme } from '@evi/skins';
import {
  register as registerApi,
  lookupTenantByJoinCode,
  type PublicTenantSummary,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/Form.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(12, 'At least 12 characters'),
  confirmPassword: z.string(),
  // Express marketing consent — optional, unchecked by default. Separate
  // from agreeing to the terms; consent is never a condition of signup.
  marketing_opt_in: z.boolean().optional(),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Passwords must match',
  path: ['confirmPassword'],
});
type FormValues = z.infer<typeof schema>;

interface Props { onNavigate: (path: string) => void }

export function RegisterPage({ onNavigate }: Props) {
  const { t } = useTranslation('auth');
  const setAuth = useAuthStore((s) => s.setAuth);
  // Registration resets the family to the brand default; the mode stays the
  // device's until the driver chooses one.
  const { setThemeId } = useTheme();
  const [serverError, setServerError] = useFormError();

  // Building join code (Session 8.c). Pulled off the URL once on
  // mount; threaded into the registration body as `join_code` so
  // the server (commit 8.b) routes the new user to the matching
  // tenant. When the lookup resolves, we surface the building name
  // inline so the user knows which building they're registering at.
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [joinTenant, setJoinTenant] = useState<PublicTenantSummary | null>(null);
  const [joinCodeInvalid, setJoinCodeInvalid] = useState(false);
  // Referral code (Friends Stage 1, share-invite UX). Read from
  // `?ref=` once on mount and threaded into the registration body
  // so the new user gets attributed to the inviter via the
  // existing referral_code system. Silently ignored server-side
  // on no-match or self-referral; we just pass it through.
  const [referralCode, setReferralCode] = useState<string | null>(null);
  // Phase 3b Item 2 — fleet invite landing handoff. When the user
  // lands on /register via an email invite link (the no-account
  // branch of /join-fleet), the URL carries the email to pre-fill
  // and the JWT, which rides the register body as fleet_invite_token.
  const [prefilledEmail, setPrefilledEmail] = useState<string | null>(null);
  const [pendingFleetToken, setPendingFleetToken] = useState<string | null>(null);
  const [homeToken, setHomeToken] = useState<string | null>(null);
  // Fleet QR / join link for a driver with no account yet. The
  // signed-out /join-fleet landing parks the code in sessionStorage and
  // sends the driver here with it on the URL as ?fleet_code=. It rides
  // the register body so the server creates the account in the FLEET'S
  // building — without it a new driver lands in the default tenant and
  // App.tsx's post-auth join drain is refused with CROSS_TENANT_INVITE.
  // The drain, not this page, performs the fleet join. No name lookup
  // on purpose: there is no public fleet-code endpoint, and there must
  // not be one (codes are not probeable), so the banner stays neutral.
  const [fleetCode, setFleetCode] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('code') ?? '';
    const normalized = raw.trim().toUpperCase();
    if (normalized && normalized.length === 6) {
      setJoinCode(normalized);
      void lookupTenantByJoinCode(normalized).then((result) => {
        if (result) setJoinTenant(result);
        else setJoinCodeInvalid(true);
      });
    }
    // The friend link's code: the ?ref= on this URL, else the one main.tsx stashed when the link opened another
    // page first (Sign in, then "Create an account" arrives here with no query). Off-shape codes are dropped; the
    // server ignores a code that matches nobody. Re-stashed so a detour to /login and Google still carries it.
    captureReferralFromUrl();
    const pendingRef = readPendingReferral();
    if (pendingRef) setReferralCode(pendingRef);
    // Fleet invite handoff.
    const emailQ = params.get('email');
    const tokenQ = params.get('pendingToken');
    if (emailQ) setPrefilledEmail(emailQ.toLowerCase());
    if (tokenQ) setPendingFleetToken(tokenQ);
    // Home-whitelist invite handoff (Residential slice C). The raw token
    // rides the register body; the server resolves the pending invite at
    // account creation. Stale tokens resolve nothing — signup proceeds.
    const homeTokenQ = params.get('homeToken');
    if (homeTokenQ) setHomeToken(homeTokenQ);
    // Fleet join-link handoff. Same normalisation + shape gate the
    // /join-fleet landing applies, so a building code in this slot is
    // dropped rather than sent to the wrong server branch. Re-stash so
    // a link that lands on /register directly (not via /join-fleet)
    // still gets drained into the fleet after the account exists.
    // The URL is one way in; the stash is the other. A driver who scanned
    // the fleet QR signed out, tapped Sign in, and then used the login
    // footer's Create account arrives here with no query but with the
    // code still parked by /join-fleet — without this fallback that
    // account would be created in the default tenant and the drain
    // refused with CROSS_TENANT_INVITE, which the driver cannot fix.
    let stashedFleetCode: string | null = null;
    try {
      stashedFleetCode = normalizeFleetJoinCode(sessionStorage.getItem(FLEET_JOIN_CODE_STORAGE_KEY));
    } catch {
      stashedFleetCode = null;
    }
    const fleetCodeQ = normalizeFleetJoinCode(params.get('fleet_code')) ?? stashedFleetCode;
    // A building code on the URL is the more specific signal and wins
    // server-side; do not also send a fleet code the server would ignore.
    if (fleetCodeQ && !(normalized && normalized.length === 6)) {
      setFleetCode(fleetCodeQ);
      try {
        sessionStorage.setItem(FLEET_JOIN_CODE_STORAGE_KEY, fleetCodeQ);
      } catch {
        /* storage blocked — placement still works; the driver can type the code after signup */
      }
    }
  }, []);

  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  // Apply the fleet-invite email pre-fill once the param resolves.
  // react-hook-form's setValue runs after the form is initialized;
  // we keep a separate state so the value sticks if the user
  // mounts the page before the param hydrates.
  useEffect(() => {
    if (prefilledEmail) setValue('email', prefilledEmail);
  }, [prefilledEmail, setValue]);

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    if (joinCodeInvalid) {
      setServerError(t('error.invalidBuildingCode'));
      return;
    }
    try {
      const res = await registerApi({
        name: values.name,
        email: values.email,
        password: values.password,
        ...(joinCode ? { join_code: joinCode } : {}),
        ...(fleetCode ? { fleet_code: fleetCode } : {}),
        ...(referralCode ? { referral_code: referralCode } : {}),
        ...(homeToken ? { home_token: homeToken } : {}),
        ...(values.marketing_opt_in ? { marketing_opt_in: true } : {}),
        ...(pendingFleetToken ? { fleet_invite_token: pendingFleetToken } : {}),
      });
      setAuth(res.user, res.access_token);
      setThemeId('default');
      // No mode is forced: a new driver keeps the device's light or dark setting until they choose (Warren, 21 October
      // 2026).
      // The parked invite has done its job (carried by this register call).
      clearStashedFleetInviteToken();

      // Phase 3b Item 2 — an email invite. When the token was valid for
      // this address the account was created in the fleet and the
      // response says so; refresh so fleet_id + fleet_name hydrate on
      // the auth store before /map renders.
      if (res.fleet_joined) {
        await useAuthStore.getState().refreshUser();
        toast(t('toast.fleetJoined', { fleetName: res.fleet_joined.fleet_name }), { tone: 'accent', durationMs: 4000 });
        onNavigate('/map');
        return;
      }
      // No fleet_joined: fall back to accept-invite, which answers
      // already_member or pending_verification (continueFleetInvite
      // routes both). Failures degrade gracefully — the user gets to
      // /profile and can re-click the invite link.
      if (pendingFleetToken) {
        try {
          if (await continueFleetInvite(pendingFleetToken, onNavigate)) return;
        } catch {
          // Fall through to normal /profile navigation.
        }
      }
      // Replay a stashed charger deep link — a brand-new guest who
      // signed up from a shared charger link lands on that charger,
      // not the profile screen.
      onNavigate(consumePendingDeepLink() ?? '/profile');
    } catch (err) {
      // INVALID_BUILDING_CODE is returned as a 404 with that
      // structured code — surface as a friendly inline message
      // instead of letting the bare server error through.
      const { code, message: serverMsg } = await readErrorEnvelope(err);
      if (code === 'INVALID_BUILDING_CODE') {
        setServerError(t('error.invalidBuildingCode'));
        return;
      }
      // A dead fleet link should not strand the driver on this page:
      // drop the code (state + stash, so the drain never posts it) and
      // the next tap creates a plain account they can join a fleet from
      // later. Unlike a building code, the fleet code was never the
      // reason they needed an account.
      if (code === 'INVALID_FLEET_CODE') {
        setFleetCode(null);
        try {
          sessionStorage.removeItem(FLEET_JOIN_CODE_STORAGE_KEY);
        } catch {
          /* storage blocked — nothing was stashed either */
        }
        setServerError(t('error.invalidFleetCode'));
        return;
      }
      // Unknown code: prefer the server's own explanation over ky's
      // "Request failed with status code ..." status line, which
      // tells a user nothing. err.message survives only for genuine
      // non-HTTP failures (network drop).
      const isHttpError = err instanceof Error && err.name === 'HTTPError';
      setServerError(
        serverMsg
        ?? (isHttpError ? t('error.registrationFailed') : err instanceof Error ? err.message : t('error.registrationFailed')),
      );
    }
  };

  return (
    <AuthLayout
      title={joinTenant
        ? t('register.titleJoin', { tenant: joinTenant.display_name ?? joinTenant.name })
        : t('register.title')}
      subtitle={joinTenant
        ? t('register.subtitleJoin')
        : t('register.subtitle')}
      footer={<>{t('register.footerPrompt')} <a href={joinCode ? `/login?join_code=${encodeURIComponent(joinCode)}` : '/login'} onClick={(e) => { e.preventDefault(); onNavigate(joinCode ? `/login?join_code=${encodeURIComponent(joinCode)}` : '/login'); }}>{t('register.footerLink')}</a></>}
    >
      <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)}>
        {joinTenant && (
          <div className={formStyles.formSuccess} role="status">
            <Building2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {t('register.joinBanner')} <strong>{joinTenant.display_name ?? joinTenant.name}</strong>
          </div>
        )}
        {joinCodeInvalid && (
          <FormError>
            {t('error.invalidBuildingCode')}
          </FormError>
        )}
        {/* Neutral by design (no fleet name — see the fleetCode note
            above). Hidden when a building code is also present: the
            building banner is the placement that will actually apply,
            and the fleet join may then be refused cross-tenant. */}
        {fleetCode && !joinCode && (
          <div className={formStyles.formSuccess} role="status">
            <Users size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {t('register.fleetNotice')}
          </div>
        )}
        {serverError && <FormError announce={serverError.nonce}>{serverError.message}</FormError>}

        <div className={formStyles.field}>
          <label className={formStyles.label}><User size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('register.nameLabel')}</label>
          <input className={formStyles.input} type="text" autoComplete="name" placeholder={t('register.namePlaceholder')} {...register('name')} />
          {errors.name && <span className={formStyles.errorMsg}>{errors.name.message}</span>}
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}><Mail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('register.emailLabel')}</label>
          <input className={formStyles.input} type="email" autoComplete="email" placeholder="you@example.com" {...register('email')} />
          {errors.email && <span className={formStyles.errorMsg}>{errors.email.message}</span>}
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}><Lock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('register.passwordLabel')}</label>
          <input className={formStyles.input} type="password" autoComplete="new-password" placeholder={t('register.passwordPlaceholder')} {...register('password')} />
          {errors.password && <span className={formStyles.errorMsg}>{errors.password.message}</span>}
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}>{t('register.confirmPasswordLabel')}</label>
          <input className={formStyles.input} type="password" autoComplete="new-password" placeholder={t('register.confirmPasswordPlaceholder')} {...register('confirmPassword')} />
          {errors.confirmPassword && <span className={formStyles.errorMsg}>{errors.confirmPassword.message}</span>}
        </div>

        <label
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            fontSize: 13, color: 'var(--color-tx-m)', cursor: 'pointer',
            margin: '2px 0',
          }}
        >
          <input type="checkbox" style={{ marginTop: 2 }} {...register('marketing_opt_in')} />
          <span>
            {t('register.marketingLead')}{' '}
            <strong style={{ color: 'var(--evi-accent)' }}>
              {t('register.marketingBonus', { points: MARKETING_OPT_IN_POINTS })}
            </strong>{' '}
            {t('register.marketingSuffix')}
          </span>
        </label>

        <button type="submit" className={formStyles.btnPrimary} disabled={isSubmitting}>
          {isSubmitting ? t('register.submitting') : t('register.submit')}
        </button>

        <p style={{ fontSize: 12, color: 'var(--color-tx-m)', textAlign: 'center', margin: '4px 0 0' }}>
          <Trans i18nKey="register.legalNotice" components={{
            tos: <a href="https://evinitiative.com/platform-terms" target="_blank" rel="noopener noreferrer" />,
            privacy: <a href="https://evinitiative.com/privacy-policy" target="_blank" rel="noopener noreferrer" />,
          }} />
          Marketing consent is separate and optional.
        </p>
      </form>
    </AuthLayout>
  );
}

/** Pull the structured error envelope out of a ky HTTPError. Both
 *  fields null when the error isn't shaped like our standard
 *  { error: { code, message } } payload. The message matters as much
 *  as the code: unknown codes must surface the server's explanation,
 *  never ky's raw status line. */
async function readErrorEnvelope(err: unknown): Promise<{ code: string | null; message: string | null }> {
  const response = (err as { response?: Response }).response;
  if (!response) return { code: null, message: null };
  try {
    const body = await response.clone().json() as { error?: { code?: string; message?: string } };
    return { code: body.error?.code ?? null, message: body.error?.message ?? null };
  } catch {
    return { code: null, message: null };
  }
}
